/**
 * Formulario de un usuario (alta y edición) con sus tres pestañas del panel
 * anterior: **Datos**, **Roles** y **Permisos Específicos**.
 *
 * Las dos últimas sólo se muestran si el usuario de la sesión puede asignarlas
 * (`usuarios.asignar_grupos` / `usuarios.asignar_permisos`), como hacía la vista
 * anterior al ocultar las pestañas; la API también lo comprueba.
 */

import {
  Briefcase,
  Check,
  Fuel,
  Image as ImageIcon,
  Search,
  ShieldCheck,
  User as UserIcon,
  Users,
} from 'lucide-react';
import { useRef, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatNumber } from '@/lib/utils';

import { urlFotoUsuario, useUsuario } from '../usuarios.api';
import type { CatalogosUsuarios, PermisosUsuarios, UsuarioPayload } from '../usuarios.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

type Pestana = 'datos' | 'roles' | 'permisos';

/** Las dos columnas de roles del formulario anterior (con su «Todos»). */
const COLUMNAS = [
  { clave: 'administracion' as const, etiqueta: 'Administración', icono: Briefcase },
  { clave: 'grifero' as const, etiqueta: 'Grifero', icono: Fuel },
];

/** Formulario ya con los datos cargados. */
function Formulario({
  usuarioId,
  datos,
  permisosIniciales,
  catalogos,
  puede,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  usuarioId: number | null;
  datos: {
    nombre: string;
    apellidos: string;
    dni: string;
    codigo: string;
    email: string;
    telefono: string;
    codigoHermes: string;
    foto: string | null;
    grupos: number[];
  };
  permisosIniciales: number[];
  catalogos: CatalogosUsuarios;
  puede: PermisosUsuarios;
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: UsuarioPayload, archivo: File | null) => void;
  onCerrar: () => void;
}) {
  const entradaFoto = useRef<HTMLInputElement>(null);

  const [pestana, setPestana] = useState<Pestana>('datos');
  const [nombre, setNombre] = useState(datos.nombre);
  const [apellidos, setApellidos] = useState(datos.apellidos);
  const [dni, setDni] = useState(datos.dni);
  const [codigo, setCodigo] = useState(datos.codigo);
  const [email, setEmail] = useState(datos.email);
  const [telefono, setTelefono] = useState(datos.telefono);
  const [codigoHermes, setCodigoHermes] = useState(datos.codigoHermes);
  const [password, setPassword] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [grupos, setGrupos] = useState<number[]>(datos.grupos);
  const [permisos, setPermisos] = useState<number[]>(permisosIniciales);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [vista, setVista] = useState<string | null>(null);
  const [busquedaGrupo, setBusquedaGrupo] = useState('');

  const esAlta = usuarioId === null;

  const alternar = (lista: number[], valor: number, activo: boolean): number[] =>
    activo ? [...lista, valor] : lista.filter((actual) => actual !== valor);

  // Las dos columnas de roles son excluyentes: con roles de Administración el usuario
  // es administrador y no puede llevar roles de Grifero (y al revés).
  const idsAdministracion = new Set(
    catalogos.grupos.filter((grupo) => grupo.padre === 'administracion').map((grupo) => grupo.id),
  );
  const esDeAdministracion = (id: number) => idsAdministracion.has(id);
  const tenemosAdministracion = grupos.some(esDeAdministracion);
  const tenemosGrifero = grupos.some((id) => !esDeAdministracion(id));

  /** Marca/desmarca un rol quitando los de la otra columna. */
  const alternarRol = (id: number, activo: boolean) => {
    setGrupos((actuales) => {
      if (!activo) return actuales.filter((actual) => actual !== id);

      const mismoLado = actuales.filter((actual) =>
        esDeAdministracion(id) === esDeAdministracion(actual),
      );

      return [...new Set([...mismoLado, id])];
    });
  };

  const faltaPassword = esAlta && password.length < 6;
  const desajuste = password !== '' && password !== confirmacion;
  const valido = nombre.trim() !== '' && apellidos.trim() !== '' && dni.trim() !== '' && !faltaPassword && !desajuste;

  const pestanas: { clave: Pestana; etiqueta: string; icono: typeof UserIcon; visible: boolean }[] = [
    { clave: 'datos', etiqueta: 'Datos', icono: UserIcon, visible: true },
    { clave: 'roles', etiqueta: 'Roles', icono: Users, visible: puede.asignar_grupos },
    { clave: 'permisos', etiqueta: 'Permisos específicos', icono: ShieldCheck, visible: puede.asignar_permisos },
  ];

  const terminoGrupo = busquedaGrupo.trim().toLowerCase();
  const gruposVisibles = terminoGrupo
    ? catalogos.grupos.filter(
        (grupo) =>
          grupo.nombre.toLowerCase().includes(terminoGrupo) ||
          grupo.descripcion.toLowerCase().includes(terminoGrupo),
      )
    : catalogos.grupos;

  const enviar = () => {
    const payload: UsuarioPayload = {
      nombre: nombre.trim(),
      apellidos: apellidos.trim(),
      dni: dni.trim(),
      codigo: codigo.trim(),
      email: email.trim(),
      telefono: telefono.trim(),
      codigo_hermes: codigoHermes.trim() === '' ? null : Number(codigoHermes),
    };

    if (password !== '') payload.password = password;
    if (puede.asignar_grupos) payload.grupos = grupos;
    if (puede.asignar_permisos) payload.permisos = permisos;

    onGuardar(payload, archivo);
  };

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={esAlta ? 'Nuevo usuario' : `Editar ${[datos.nombre, datos.apellidos].filter(Boolean).join(' ')}`}
      description="Los roles dan acceso a las pantallas; los permisos específicos habilitan acciones concretas dentro de ellas."
      size="lg"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button size="sm" loading={guardando} disabled={!valido} onClick={enviar}>
            {esAlta ? 'Crear usuario' : 'Guardar cambios'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        <div className="flex gap-1 rounded-control bg-ink-50 p-1">
          {pestanas
            .filter((item) => item.visible)
            .map((item) => (
              <button
                key={item.clave}
                type="button"
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded-[10px] px-2 py-1.5 text-xs font-semibold transition-colors',
                  pestana === item.clave
                    ? 'bg-white text-brand-700 shadow-soft'
                    : 'text-ink-500 hover:text-ink-700',
                )}
                onClick={() => setPestana(item.clave)}
              >
                <item.icono className="size-3.5" aria-hidden="true" />
                {item.etiqueta}
              </button>
            ))}
        </div>

        {pestana === 'datos' ? (
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="DNI"
                value={dni}
                maxLength={12}
                autoFocus
                placeholder="Número de DNI"
                onChange={(evento) => setDni(evento.target.value)}
              />
              <Input
                label="Código de usuario"
                value={codigo}
                maxLength={13}
                placeholder={esAlta ? 'Se genera si lo dejas vacío' : 'Código'}
                hint={esAlta ? '4 a 13 caracteres; si lo dejas vacío se genera con el DNI' : undefined}
                onChange={(evento) => setCodigo(evento.target.value)}
              />
              <Input
                label="Nombres"
                value={nombre}
                maxLength={50}
                onChange={(evento) => setNombre(evento.target.value)}
              />
              <Input
                label="Apellidos"
                value={apellidos}
                maxLength={50}
                onChange={(evento) => setApellidos(evento.target.value)}
              />
              <Input
                label="Correo electrónico"
                type="email"
                value={email}
                onChange={(evento) => setEmail(evento.target.value)}
              />
              <Input
                label="Teléfono"
                value={telefono}
                maxLength={30}
                onChange={(evento) => setTelefono(evento.target.value)}
              />
              <Input
                label="Código Hermes"
                value={codigoHermes}
                placeholder="Opcional"
                hint="Identificador del usuario en el sistema Hermes"
                onChange={(evento) => setCodigoHermes(evento.target.value.replace(/[^\d]/g, ''))}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label={esAlta ? 'Contraseña' : 'Nueva contraseña'}
                type="password"
                value={password}
                autoComplete="new-password"
                placeholder={esAlta ? 'Mínimo 6 caracteres' : 'Dejar en blanco para mantenerla'}
                onChange={(evento) => setPassword(evento.target.value)}
              />
              <Input
                label="Confirmar contraseña"
                type="password"
                value={confirmacion}
                autoComplete="new-password"
                error={desajuste ? 'Las contraseñas no coinciden' : undefined}
                onChange={(evento) => setConfirmacion(evento.target.value)}
              />
            </div>

            <div className="flex items-center gap-4 rounded-control border border-ink-100 p-3">
              <div className="flex size-20 items-center justify-center overflow-hidden rounded-card bg-ink-50 ring-1 ring-ink-100">
                {vista ? (
                  <img src={vista} alt="Vista previa" className="size-full object-cover" />
                ) : datos.foto && usuarioId ? (
                  <img
                    src={urlFotoUsuario(usuarioId, datos.foto)}
                    alt="Foto del usuario"
                    className="size-full object-cover"
                  />
                ) : (
                  <ImageIcon className="size-6 text-ink-300" aria-hidden="true" />
                )}
              </div>

              <div className="flex flex-col gap-2">
                <input
                  ref={entradaFoto}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(evento) => {
                    const elegido = evento.target.files?.[0] ?? null;
                    setArchivo(elegido);
                    setVista(elegido ? URL.createObjectURL(elegido) : null);
                  }}
                />
                <Button variant="outline" size="sm" onClick={() => entradaFoto.current?.click()}>
                  {archivo ? 'Cambiar imagen' : 'Elegir imagen'}
                </Button>
                <p className="text-xs text-ink-400">
                  JPG, PNG o WEBP · máximo 6 MB · se sube al guardar
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {pestana === 'roles' ? (
          <div className="flex flex-col gap-3">
            <Alert variant="info">Seleccione los roles que tendrá el usuario en el sistema.</Alert>

            <Input
              leftIcon={<Search className="size-4" aria-hidden="true" />}
              placeholder="Buscar grupo…"
              value={busquedaGrupo}
              onChange={(evento) => setBusquedaGrupo(evento.target.value)}
            />

            <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
              <Badge variant="neutral">{grupos.length} seleccionado(s)</Badge>
              <span>
                Las dos columnas son <strong>excluyentes</strong>: al marcar un rol de una se
                quitan los de la otra, porque el usuario es administrador o es grifero.
              </span>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {COLUMNAS.map((columna) => {
                const opciones = gruposVisibles.filter((grupo) => grupo.padre === columna.clave);
                const ids = opciones.map((grupo) => grupo.id);
                const todos = ids.length > 0 && ids.every((id) => grupos.includes(id));
                const esAdministracion = columna.clave === 'administracion';
                const bloqueada = esAdministracion ? tenemosGrifero : tenemosAdministracion;

                return (
                  <div key={columna.clave} className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={cn(
                          'flex items-center gap-1.5 text-sm font-semibold',
                          esAdministracion ? 'text-brand-700' : 'text-emerald-700',
                        )}
                      >
                        <columna.icono className="size-4" aria-hidden="true" />
                        {columna.etiqueta}
                        <span className="text-[11px] font-normal text-ink-400">({ids.length})</span>
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-[11px]"
                        leftIcon={<Check className="size-3" aria-hidden="true" />}
                        disabled={ids.length === 0}
                        onClick={() =>
                          setGrupos((actuales) => {
                            const mismoLado = actuales.filter(
                              (actual) => esDeAdministracion(actual) === esAdministracion,
                            );

                            return todos ? mismoLado : [...new Set([...mismoLado, ...ids])];
                          })
                        }
                      >
                        {todos ? 'Quitar todos' : 'Todos'}
                      </Button>
                    </div>

                    {bloqueada ? (
                      <p className="rounded-control bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                        Al marcar un rol de {columna.etiqueta} se quitarán los de{' '}
                        {esAdministracion ? 'Grifero' : 'Administración'}.
                      </p>
                    ) : null}

                    <div
                      className={cn(
                        'max-h-64 overflow-y-auto rounded-control border border-ink-100 transition-opacity',
                        bloqueada && 'opacity-60',
                      )}
                    >
                      {opciones.length === 0 ? (
                        <p className="px-3 py-4 text-center text-xs text-ink-400">
                          {terminoGrupo ? 'Sin coincidencias' : 'Sin roles en esta columna'}
                        </p>
                      ) : (
                        opciones.map((grupo) => (
                          <label
                            key={grupo.id}
                            className="flex cursor-pointer items-start gap-3 border-b border-ink-100 px-3 py-2 last:border-b-0 hover:bg-ink-50/60"
                          >
                            <Checkbox
                              label=""
                              aria-label={`Asignar ${grupo.nombre}`}
                              checked={grupos.includes(grupo.id)}
                              onChange={(evento) => alternarRol(grupo.id, evento.target.checked)}
                            />
                            <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-medium text-ink-800">
                                  {grupo.nombre}
                                </span>
                                <span className="block truncate text-xs text-ink-500">
                                  {grupo.descripcion}
                                </span>
                              </span>
                              <span className="flex shrink-0 items-center gap-2">
                                <Badge variant={grupo.tipo === 'tipo' ? 'danger' : 'outline'}>
                                  {grupo.tipo === 'tipo' ? 'Rol' : 'Módulo'}
                                </Badge>
                                <span className="text-[11px] text-ink-400">
                                  {formatNumber(grupo.usuarios)}
                                </span>
                              </span>
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {pestana === 'permisos' ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
              <Badge variant="neutral">{permisos.length} concedido(s)</Badge>
              <span>Los permisos específicos habilitan acciones puntuales (verificar, anular…).</span>
            </div>

            <div className="max-h-72 space-y-3 overflow-y-auto rounded-control border border-ink-100 p-3">
              {catalogos.permisos.map((bloque) => (
                <div key={bloque.modulo} className="space-y-1.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    {bloque.modulo}
                  </p>
                  {bloque.permisos.map((permiso) => (
                    <label
                      key={permiso.id}
                      className="flex cursor-pointer items-start gap-3 rounded-control px-2 py-1.5 hover:bg-ink-50/60"
                    >
                      <Checkbox
                        label=""
                        aria-label={`Conceder ${permiso.codigo}`}
                        checked={permisos.includes(permiso.id)}
                        onChange={(evento) =>
                          setPermisos((actuales) => alternar(actuales, permiso.id, evento.target.checked))
                        }
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-ink-800">{permiso.nombre}</span>
                        <span className="block text-xs text-ink-500">
                          {permiso.codigo}
                          {permiso.descripcion ? ` · ${permiso.descripcion}` : ''}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Diálogo de usuario: carga el detalle cuando se edita y monta el formulario. */
export function UsuarioDialogo({
  usuarioId,
  catalogos,
  puede,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  usuarioId: number | null;
  catalogos: CatalogosUsuarios;
  puede: PermisosUsuarios;
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: UsuarioPayload, archivo: File | null) => void;
  onCerrar: () => void;
}) {
  const detalle = useUsuario(usuarioId);

  const vacio = {
    nombre: '',
    apellidos: '',
    dni: '',
    codigo: '',
    email: '',
    telefono: '',
    codigoHermes: '',
    foto: null,
    grupos: [] as number[],
  };

  if (usuarioId !== null && detalle.isLoading) {
    return (
      <Dialog open onClose={onCerrar} title="Cargando usuario…" size="lg">
        <Skeleton className="h-64 w-full" />
      </Dialog>
    );
  }

  if (usuarioId !== null && detalle.error) {
    return (
      <Dialog
        open
        onClose={onCerrar}
        title="Editar usuario"
        footer={
          <Button variant="ghost" size="sm" onClick={onCerrar}>
            Cerrar
          </Button>
        }
      >
        <Alert variant="error" title="No se pudo leer el usuario">
          {mensajeDe(detalle.error, 'La API no respondió.')}
        </Alert>
      </Dialog>
    );
  }

  const usuario = detalle.data?.usuario ?? null;

  return (
    <Formulario
      key={`usuario-${usuarioId ?? 'nuevo'}`}
      usuarioId={usuarioId}
      datos={
        usuario
          ? {
              nombre: usuario.nombre,
              apellidos: usuario.apellidos,
              dni: usuario.dni,
              codigo: usuario.codigo,
              email: usuario.email,
              telefono: usuario.telefono,
              codigoHermes: usuario.codigo_hermes === null ? '' : String(usuario.codigo_hermes),
              foto: usuario.foto,
              grupos: usuario.grupos,
            }
          : vacio
      }
      permisosIniciales={detalle.data?.permisos ?? []}
      catalogos={catalogos}
      puede={puede}
      guardando={guardando}
      error={error}
      onGuardar={onGuardar}
      onCerrar={onCerrar}
    />
  );
}
